import type { KeyboardEvent } from 'react';
import { useEffect, useState } from 'react';

import type { AssistantSuggestionMessage } from '@/entities/discovery';
import { Button } from '@/shared/components/shadcn/button';
import { Input } from '@/shared/components/shadcn/input';
import { cn } from '@/shared/utils';
import { ArrowRightIcon } from 'lucide-react';
import { AssistantMessageRow } from './primitives';
import { useAnalyzeDiscoveryPrompt } from '../../hooks/use-analyze-discovery-prompt';

export const AssistantMessageSuggestion = ({
  message,
  selected_prompt,
}: {
  message: AssistantSuggestionMessage;
  selected_prompt?: string;
}) => {
  const [tempSelectedPrompt, setTempSelectedPrompt] = useState<string | undefined>(selected_prompt);
  const analyzeDiscovery = useAnalyzeDiscoveryPrompt();
  const selectedPrompt = tempSelectedPrompt ?? selected_prompt;
  const customSelectedPrompt =
    selectedPrompt && !message.content.suggested_prompts.includes(selectedPrompt)
      ? selectedPrompt
      : undefined;

  const handleSelect = async (prompt: string) => {
    setTempSelectedPrompt(prompt);
    try {
      await analyzeDiscovery.mutateAsync({
        discoverySessionId: message.discovery_session_id,
        request: {
          message: prompt,
        },
      });
    } catch (error) {
      setTempSelectedPrompt(undefined);
      throw error;
    }
  };

  return (
    <AssistantMessageRow>
      <div className="mt-1 flex flex-1 flex-col gap-3">
        <span className="text-primary text-sm">{message.content.message}</span>
        <div className="flex flex-col gap-2">
          {message.content.suggested_prompts.map((suggestion) => (
            <SuggestionCard
              key={suggestion}
              text={suggestion}
              selected={selectedPrompt === suggestion}
              disabled={Boolean(selectedPrompt) && selectedPrompt !== suggestion}
              onClick={() => handleSelect(suggestion)}
            />
          ))}
          <CustomPromptSuggestionInput
            selectedValue={customSelectedPrompt}
            disabled={selectedPrompt != null}
            onSubmit={handleSelect}
          />
        </div>
      </div>
    </AssistantMessageRow>
  );
};

function SuggestionCard({
  text,
  selected = false,
  disabled = false,
  onClick,
}: {
  text: string;
  selected?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'bg-background border-border group flex w-full items-center gap-2 rounded-lg border px-4 py-3 text-left transition-colors',
        selected && 'border-muted-foreground bg-muted',
        disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-muted',
      )}
    >
      <p className="text-primary flex-1 text-sm font-medium">{text}</p>
      <span
        aria-hidden="true"
        className={cn(
          'bg-secondary text-secondary-foreground flex size-8 shrink-0 items-center justify-center rounded-md transition-colors',
          disabled ? 'opacity-50' : 'group-hover:bg-secondary/80',
        )}
      >
        <ArrowRightIcon className="text-primary size-4" />
      </span>
    </button>
  );
}

function CustomPromptSuggestionInput({
  selectedValue,
  disabled,
  onSubmit,
}: {
  selectedValue?: string;
  disabled?: boolean;
  onSubmit: (prompt: string) => void;
}) {
  const [value, setValue] = useState(selectedValue ?? '');
  const isSelected = selectedValue != null;
  const trimmedValue = value.trim();
  const canSubmit = trimmedValue.length > 0 && !disabled;

  useEffect(() => {
    setValue(selectedValue ?? '');
  }, [selectedValue]);

  const handleSubmit = () => {
    if (!canSubmit) return;

    onSubmit(trimmedValue);
    setValue('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div
      className={cn(
        'bg-background border-border flex min-h-14 items-center gap-2 rounded-lg border px-4 py-3 transition-colors',
        isSelected && 'border-muted-foreground bg-muted',
        disabled && 'opacity-60',
      )}
    >
      <Input
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Describe what you want instead"
        aria-label="Custom prompt"
        disabled={disabled}
        className="text-primary h-auto flex-1 border-0 bg-transparent px-0 py-0 text-sm font-medium shadow-none focus-visible:border-transparent focus-visible:ring-0 disabled:opacity-100"
      />
      <Button
        type="button"
        size="icon"
        variant="secondary"
        className="size-8 shrink-0"
        disabled={!canSubmit}
        onClick={handleSubmit}
      >
        <ArrowRightIcon className="text-primary size-4" />
        <span className="sr-only">Submit custom prompt</span>
      </Button>
    </div>
  );
}
